import { useEffect, useMemo } from 'react'
import { useResizeDetector } from 'react-resize-detector'

import { GraphDefaultProps, GraphRender, GraphType } from '../common'
import { GraphDirection } from '../../../../types'
import { BarGraphOptions, useBarGraphOption } from './container/options'
import { useBarGraphModel } from './container/model'
import { useBarGraphRenderOption } from './container/render'
import { useGraphZoom } from '../../components/zoom'
import { useBarGraphEvents } from './container/event'

import ReactEChartsCore from 'echarts-for-react/lib/core'
import * as echarts from 'echarts/core'
import { BarChart } from 'echarts/charts'
import { CanvasRenderer } from 'echarts/renderers'

import {
  TooltipComponent,
  GridComponent,
  LegendComponent,
  DataZoomComponent
} from 'echarts/components'

import { Box } from '@mui/material'

echarts.use([
  TooltipComponent,
  GridComponent,
  LegendComponent,
  DataZoomComponent,
  BarChart,
  CanvasRenderer,
])

const BarGraph = <T,>({
  direction,
  dashBoard,
  targetList,
  dataList,
  conditions,
  onChangeCondition,
  itemData,
  isResizing,
  graphRef,
  setCsv
}: GraphDefaultProps<T> & {
  direction: GraphDirection
}) => {
  const { options, filterText } = itemData
  const { width, height, ref } = useResizeDetector()

  const maxRow = useMemo(
    () => height ? (
      direction === "horizontal" ? (
        height >= 1000 ? 50 :
        height >= 800 ? 40 :
        height >= 600 ? 30 :
        height >= 300 ? 20 :
        height >= 100 ? 10 : 5
      ) : 20
    ) : 1,
    [direction, height]
  )
  const graphOption = useBarGraphOption({ direction, dashBoard, options, targetList })
  const graphData = useBarGraphModel({ dataList, conditions, filterText, graphOption })
  const graphZoom = useGraphZoom({ direction, maxRow, dataLength: graphData?.data.length })
  const renderOption = useBarGraphRenderOption({ height: height ?? 0, graphOption, graphData, graphZoom })
  const events = useBarGraphEvents({ graphOption, graphZoom, onChangeCondition })

  useEffect(
    () => setCsv({ builder: () => graphData?.csvBuilder() }),
    [graphData, setCsv]
  )

  const { axisDef, stackDef, valueDef } = graphOption
  return (
    <Box ref={ref} width="100%" height="100%">
      <GraphRender
        dashBoard={dashBoard}
        keys={[axisDef?.key, valueDef?.key]}
        optionKeys={[stackDef?.key]}
        invalid={!graphData || isResizing || ((height ?? 0) <= 10)}
      >
        {(width && height) ? (
          <ReactEChartsCore
            ref={graphRef}
            echarts={echarts}
            notMerge={true}
            onEvents={events}
            option={renderOption}
            opts={{ renderer: 'canvas' }}
            style={{ width, height }}
            theme="insite"
          />
        ) : <></>}
      </GraphRender>
    </Box>
  )
}
BarGraph.displayName = 'echartsCustomGraph'

export const HorizontalBarGraphFactory: GraphType = {
  title: '水平棒グラフ',
  options: BarGraphOptions,
  element: <T,>(props: GraphDefaultProps<T>) => <BarGraph direction='horizontal' {...props} />
}
export const VerticalBarGraphFactory: GraphType = {
  title: '垂直棒グラフ',
  options: BarGraphOptions,
  element: <T,>(props: GraphDefaultProps<T>) => <BarGraph direction='vertical' {...props} />
}
